import { useState, useEffect, useRef } from 'react';
import { X, Printer, Save, Link2, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';

interface CoextrusionModalProps {
  productionOrderId: string;
  onClose: () => void;
}

// ── Standalone autocomplete input for materia prima ───────────────────────────
// Must be defined OUTSIDE CoextrusionModal so React doesn't remount it on every
// parent re-render (which would cause the input to lose focus while typing).
interface MatInputProps {
  value: string;
  onChange: (val: string) => void;
}
// ROT-185: used to filter a single shared, pre-fetched page of up to 500
// MATERIA_PRIMA catalog products (passed in as `catalogMaterials`) — with
// more than 500 real rows for this tenant, sorted by name, anything past the
// 500th (any name starting with a lowercase letter, since it sorts after
// every uppercase one) was silently unsearchable no matter what was typed.
// Now queries the backend directly per keystroke (debounced), same
// endpoint/pattern ProductionOrders.tsx's material autocomplete already
// uses, which has no such cap.
function MatInputWithDropdown({ value, onChange }: MatInputProps) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const query = (value || '').trim();

  useEffect(() => {
    if (!open || query.length < 1) { setSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/catalogs/materials?search=${encodeURIComponent(query)}&limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSuggestions(await res.json());
      } catch (e) {
        console.error('Error searching materials', e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={wrapperRef} className="relative w-full">
      <input
        className="w-full outline-none bg-transparent px-1 text-xs"
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Buscar en catálogo..."
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute left-0 top-full mt-0.5 z-[200] bg-white border border-gray-300 rounded shadow-xl w-80 max-h-52 overflow-y-auto">
          {suggestions.map((mat, si) => (
            <button
              key={si}
              type="button"
              onMouseDown={e => { e.preventDefault(); onChange(mat.name); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 hover:bg-blue-50 border-b border-gray-100 last:border-0"
            >
              <span className="font-medium text-gray-800 text-xs">{mat.name}</span>
              {mat.code && <span className="ml-2 text-gray-400 text-[10px]">{mat.code}</span>}
              {mat.caliber && <span className="ml-1 text-gray-400 text-[10px]">· cal {mat.caliber}</span>}
            </button>
          ))}
        </div>
      )}
      {open && query.length >= 2 && suggestions.length === 0 && (
        <div className="absolute left-0 top-full mt-0.5 z-[200] bg-white border border-gray-300 rounded shadow-xl w-64 px-3 py-2 text-xs text-gray-400">
          Sin resultados — puedes escribir el nombre manualmente
        </div>
      )}
    </div>
  );
}

const EMPTY_ROW = { materiaPrima: '', pctFormulacion: '', pctResina: '', cantidad: '' };
const DEFAULT_ROWS = 6;

function makeRows(n = DEFAULT_ROWS) {
  return Array.from({ length: n }, () => ({ ...EMPTY_ROW }));
}

// Helper: check if a caraTratada/caraSello array has any actual material names
function hasFilledRows(rows: any[]) {
  return Array.isArray(rows) && rows.some(r => (r.materiaPrima || '').trim() !== '');
}

// Helper: build prefill rows for one face from a list of face-specific materials
function buildFaceRows(faceMats: any[], faceTotal: number) {
  const rows = faceMats.map((m: any) => {
    const qty = parseFloat(m.quantity || '0');
    const pct = faceTotal > 0 ? ((qty / faceTotal) * 100).toFixed(2) : '';
    return {
      materiaPrima: m.material || '',
      pctFormulacion: pct,
      pctResina: pct,
      cantidad: qty > 0 ? qty.toFixed(2) : '',
    };
  });
  while (rows.length < DEFAULT_ROWS) rows.push({ ...EMPTY_ROW });
  return rows.slice(0, Math.max(DEFAULT_ROWS, rows.length));
}

// Helper: split materials by caraNombre and build prefill for both faces
function buildPrefillByFace(mats: any[]) {
  const normalize = (s: string) => s?.toUpperCase().trim() || '';
  const CT = mats.filter(m => normalize(m.caraNombre).includes('CARA TRATADA'));
  const CS = mats.filter(m => normalize(m.caraNombre).includes('CARA DE SELLO'));
  const hasFaceInfo = CT.length > 0 || CS.length > 0;

  if (!hasFaceInfo) {
    // No face info — put all in both faces as fallback
    const grandTotal = mats.reduce((s, m) => s + parseFloat(m.quantity || '0'), 0);
    const rows = buildFaceRows(mats, grandTotal);
    return { ct: rows, cs: buildFaceRows(mats, grandTotal), pctTratada: '50', pctSello: '50' };
  }

  const totalCT = CT.reduce((s, m) => s + parseFloat(m.quantity || '0'), 0);
  const totalCS = CS.reduce((s, m) => s + parseFloat(m.quantity || '0'), 0);
  const grandTotal = totalCT + totalCS;
  const pctTratada = grandTotal > 0 ? ((totalCT / grandTotal) * 100).toFixed(2) : '50';
  const pctSello = grandTotal > 0 ? ((totalCS / grandTotal) * 100).toFixed(2) : '50';

  return {
    ct: buildFaceRows(CT, totalCT),
    cs: buildFaceRows(CS, totalCS),
    pctTratada,
    pctSello,
  };
}

export default function CoextrusionModal({ productionOrderId, onClose }: CoextrusionModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);
  const [linkableOrders, setLinkableOrders] = useState<any[]>([]);
  const [showLinkPanel, setShowLinkPanel] = useState(false);

  // Form state
  const [formulaName, setFormulaName] = useState('');
  const [ancho, setAncho] = useState('');
  const [calibre, setCalibre] = useState('');
  const [color, setColor] = useState('TRANSPARENTE');
  const [inventario, setInventario] = useState('0');
  const [factorDesperdicio, setFactorDesperdicio] = useState('10');
  const [pctCaraTratada, setPctCaraTratada] = useState('50');
  const [pctCaraSello, setPctCaraSello] = useState('50');
  const [pesoEstructura, setPesoEstructura] = useState('');
  const [rendimiento, setRendimiento] = useState('');
  const [micraje, setMicraje] = useState('');
  const [desperdicio, setDesperdicio] = useState('');
  const [caraTratada, setCaraTratada] = useState<any[]>(makeRows());
  const [caraSello, setCaraSello] = useState<any[]>(makeRows());
  const [corteLateral, setCorteLateral] = useState(false);
  const [refilado, setRefilado] = useState(false);
  const [refiladoCms, setRefiladoCms] = useState('');
  const [tubular, setTubular] = useState(false);
  const [pesoBobina, setPesoBobina] = useState('');
  const [diametroBobina, setDiametroBobina] = useState('');
  const [metros, setMetros] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [saveAsRecipe, setSaveAsRecipe] = useState(true);
  const [linkedOrderIds, setLinkedOrderIds] = useState<string[]>([]);
  const [hasExisting, setHasExisting] = useState(false);
  const [recipeApplied, setRecipeApplied] = useState(false);
  const [pendingRecipe, setPendingRecipe] = useState<any>(null);
  const [recipeSearching, setRecipeSearching] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productSuggestions, setProductSuggestions] = useState<any[]>([]);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedProductCode, setSelectedProductCode] = useState('');
  const [linkSearch, setLinkSearch] = useState('');
  const productInputRef = useRef<HTMLInputElement>(null);
  const recipeSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Recalculate pctResina and cantidad for all rows given cara% and factorDesperdicio
  // pctResina = pctFormulacion × pctCara / 100
  // cantidad  = OProduccion × (pctCara/100) × (pctFormulacion/100) × (1 + factorDesp/100)
  const recalcRows = (rows: any[], pctCara: number, totalKg: number, factorDesp: number) =>
    rows.map(r => {
      const pctForm = parseFloat(r.pctFormulacion) || 0;
      const pctResina = pctForm > 0 ? ((pctForm * pctCara) / 100).toFixed(4) : '';
      const cantidad = pctForm > 0 && totalKg > 0
        ? (totalKg * (pctCara / 100) * (pctForm / 100) * (1 + factorDesp / 100)).toFixed(2)
        : '';
      return { ...r, pctResina, cantidad };
    });

  // Load all data on open
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      try {
        const [dataRes, linkRes] = await Promise.all([
          fetch(`/api/coextrusion-orders/${productionOrderId}`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/coextrusion-orders/linkable-orders', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const data = await dataRes.json();
        const links = linkRes.ok ? await linkRes.json() : [];
        setLinkableOrders(links);
        setOrderData(data);
        setSelectedProductName(data.order?.productName || '');
        setSelectedProductCode(data.order?.productCode || '');
        setProductSearch(data.order?.productName || '');

        const totalKg = parseFloat(data.order?.quantityPlanned || '0');

        if (data.existing) {
          // Load saved coextrusion
          setHasExisting(true);
          const fd = data.existing.formData || {};
          if (fd.productName) {
            setSelectedProductName(fd.productName);
            setProductSearch(fd.productName);
          }
          setFormulaName(fd.formulaName || '');
          // ROT-206: a saved order whose ancho/calibre were never filled in
          // (e.g. saved before Comercia had captured them) used to stay
          // blank forever on every reopen — only the fresh-order branch
          // below fell back to data.comerciaAncho/comerciaCalibre. Apply the
          // same fallback here so a later Comercia refresh that fills in
          // notes.comerciaAncho/comerciaCalibre actually surfaces.
          setAncho(fd.ancho || data.comerciaAncho || '');
          setCalibre(fd.calibre || data.comerciaCalibre || '');
          setColor(fd.color || 'TRANSPARENTE');
          setInventario(fd.inventario || '0');
          setFactorDesperdicio(fd.factorDesperdicio || '10');
          setPctCaraTratada(fd.porcentajeCaraTratada || '50');
          setPctCaraSello(fd.porcentajeCaraSello || '50');
          setPesoEstructura(fd.pesoEstructura || '');
          setRendimiento(fd.rendimiento || '');
          setMicraje(fd.micraje || '');
          setDesperdicio(fd.desperdicio || '');
          setCorteLateral(fd.corteLateral || false);
          setRefilado(fd.refilado || false);
          setRefiladoCms(fd.refiladoCms || '');
          setTubular(fd.tubular || false);
          setPesoBobina(fd.pesoBobina || '');
          setDiametroBobina(fd.diametroBobina || '');
          setMetros(fd.metros || '');
          setObservaciones(fd.observaciones || '');
          setLinkedOrderIds(data.existing.linkedOrderIds || []);

          // If the saved tables are empty (saved before materials arrived), fall back to materials
          const savedCT = fd.caraTratada;
          const savedCS = fd.caraSello;
          const mats = data.materials || [];
          if (!hasFilledRows(savedCT) && !hasFilledRows(savedCS) && mats.length > 0) {
            const { ct, cs, pctTratada, pctSello } = buildPrefillByFace(mats);
            setCaraTratada(ct);
            setCaraSello(cs);
            setPctCaraTratada(pctTratada);
            setPctCaraSello(pctSello);
            if (mats[0]?.caliber && !fd.calibre) setCalibre(mats[0].caliber);
          } else {
            setCaraTratada(savedCT && savedCT.length > 0 ? savedCT : makeRows());
            setCaraSello(savedCS && savedCS.length > 0 ? savedCS : makeRows());
          }
        } else if (data.recipe) {
          // Apply recipe
          setRecipeApplied(true);
          const rd = data.recipe.recipeData || {};
          setFormulaName(rd.formulaName || '');
          setAncho(rd.ancho || '');
          setCalibre(rd.calibre || '');
          setColor(rd.color || 'TRANSPARENTE');
          const rdFactor = parseFloat(rd.factorDesperdicio || '10');
          const rdPctTratada = parseFloat(rd.porcentajeCaraTratada || '50');
          const rdPctSello = parseFloat(rd.porcentajeCaraSello || '50');
          setFactorDesperdicio(String(rdFactor));
          setPctCaraTratada(String(rdPctTratada));
          setPctCaraSello(String(rdPctSello));
          setCalibre(rd.calibre || '');
          // Re-calculate pctResina and cantidades with actual totalKg and cara %
          setCaraTratada(rd.caraTratada
            ? recalcRows(rd.caraTratada, rdPctTratada, totalKg, rdFactor)
            : makeRows());
          setCaraSello(rd.caraSello
            ? recalcRows(rd.caraSello, rdPctSello, totalKg, rdFactor)
            : makeRows());
        } else {
          // Pre-fill from materials (requerimientosMateriales already stored in productionOrderMaterials)
          const mats = data.materials || [];
          if (mats.length > 0) {
            const { ct, cs, pctTratada, pctSello } = buildPrefillByFace(mats);
            setCaraTratada(ct);
            setCaraSello(cs);
            setPctCaraTratada(pctTratada);
            setPctCaraSello(pctSello);
          }
          // Pre-fill Ancho/Calibre from Comercia's own order-level
          // characteristics for THIS product (ROT-183) — these are the
          // co-extruded product's own values, not a raw material's, so they
          // take priority over the material-based calibre fallback below.
          if (data.comerciaAncho) setAncho(data.comerciaAncho);
          if (data.comerciaCalibre) setCalibre(data.comerciaCalibre);
          // Pre-fill calibre from first material that has one, only if
          // Comercia didn't already supply the order's own calibre above.
          if (!data.comerciaCalibre) {
            const firstWithCaliber = mats.find((m: any) => m.caliber && parseFloat(m.caliber) > 0);
            if (firstWithCaliber?.caliber) setCalibre(firstWithCaliber.caliber);
          }
        }
      } catch (e) {
        console.error('Error loading coextrusion data', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionOrderId]);

  // ROT-185: the "PRODUCTO (editable)" search used to filter a single
  // pre-fetched page of up to 500 MATERIA_PRIMA catalog products client-side
  // — with 866 real rows for this tenant, anything past the 500th (sorted by
  // name, so any name starting with a lowercase letter falls after every
  // uppercase one) was silently unsearchable no matter what was typed. Query
  // the backend instead, same debounced-search endpoint/pattern already used
  // by ProductionOrders.tsx's material autocomplete, which has no such cap.
  useEffect(() => {
    if (!productDropdownOpen || !productSearch.trim()) { setProductSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/catalogs/materials?search=${encodeURIComponent(productSearch.trim())}&limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setProductSuggestions(await res.json());
      } catch (e) {
        console.error('Error searching materials', e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, productDropdownOpen]);

  // Auto-calculate desperdicio: OProduccion × factorDesperdicio/100
  useEffect(() => {
    if (!orderData) return;
    const totalKg = parseFloat(orderData.order?.quantityPlanned || '0');
    const factor = parseFloat(factorDesperdicio || '0');
    if (totalKg > 0) setDesperdicio(((totalKg * factor) / 100).toFixed(2));
  }, [factorDesperdicio, orderData]);

  // Auto-calculate micraje, pesoEstructura, rendimiento from calibre
  // micraje = calibre / 4 ; pesoEstructura = micraje × 0.92 ; rendimiento = 1000 / pesoEstructura
  useEffect(() => {
    const cal = parseFloat(calibre) || 0;
    if (cal > 0) {
      const mic = cal / 4;
      const peso = mic * 0.92;
      const rend = peso > 0 ? 1000 / peso : 0;
      setMicraje(mic.toFixed(2));
      setPesoEstructura(peso.toFixed(2));
      setRendimiento(rend.toFixed(2));
    } else {
      setMicraje('');
      setPesoEstructura('');
      setRendimiento('');
    }
  }, [calibre]);

  // Recalculate all rows when pctCaraTratada, pctCaraSello, or factorDesperdicio changes
  useEffect(() => {
    if (!orderData) return;
    const totalKg = parseFloat(orderData.order?.quantityPlanned || '0');
    const pctT = parseFloat(pctCaraTratada) || 0;
    const factor = parseFloat(factorDesperdicio) || 0;
    setCaraTratada(prev => recalcRows(prev, pctT, totalKg, factor));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pctCaraTratada, factorDesperdicio, orderData]);

  useEffect(() => {
    if (!orderData) return;
    const totalKg = parseFloat(orderData.order?.quantityPlanned || '0');
    const pctS = parseFloat(pctCaraSello) || 0;
    const factor = parseFloat(factorDesperdicio) || 0;
    setCaraSello(prev => recalcRows(prev, pctS, totalKg, factor));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pctCaraSello, factorDesperdicio, orderData]);

  const updateRow = (which: 'tratada' | 'sello', idx: number, field: string, value: string) => {
    const totalKg = parseFloat(orderData?.order?.quantityPlanned || '0');
    const setter = which === 'tratada' ? setCaraTratada : setCaraSello;
    const pctCara = parseFloat(which === 'tratada' ? pctCaraTratada : pctCaraSello) || 0;
    const factor = parseFloat(factorDesperdicio) || 0;
    const rows = which === 'tratada' ? [...caraTratada] : [...caraSello];
    rows[idx] = { ...rows[idx], [field]: value };
    // When % formulacion changes, auto-recalc pctResina and cantidad
    if (field === 'pctFormulacion') {
      const pctForm = parseFloat(value) || 0;
      rows[idx].pctResina = pctForm > 0 ? ((pctForm * pctCara) / 100).toFixed(4) : '';
      rows[idx].cantidad = pctForm > 0 && totalKg > 0
        ? (totalKg * (pctCara / 100) * (pctForm / 100) * (1 + factor / 100)).toFixed(2)
        : '';
    }
    setter(rows);
  };

  // Apply a recipe's data to the form state
  const applyRecipe = (rd: any) => {
    const tkgNow = parseFloat(orderData?.order?.quantityPlanned || '0');
    const rdFactor = parseFloat(rd.factorDesperdicio || '10');
    const rdPctT = parseFloat(rd.porcentajeCaraTratada || '50');
    const rdPctS = parseFloat(rd.porcentajeCaraSello || '50');
    if (rd.formulaName) setFormulaName(rd.formulaName);
    if (rd.ancho) setAncho(rd.ancho);
    if (rd.calibre) setCalibre(rd.calibre);
    if (rd.color) setColor(rd.color);
    setFactorDesperdicio(String(rdFactor));
    setPctCaraTratada(String(rdPctT));
    setPctCaraSello(String(rdPctS));
    setCaraTratada(rd.caraTratada
      ? recalcRows(rd.caraTratada, rdPctT, tkgNow, rdFactor)
      : makeRows());
    setCaraSello(rd.caraSello
      ? recalcRows(rd.caraSello, rdPctS, tkgNow, rdFactor)
      : makeRows());
    setRecipeApplied(true);
    setPendingRecipe(null);
  };

  // When product selection changes, search for a recipe (debounced 600ms)
  useEffect(() => {
    if (!orderData || hasExisting) return;
    if (!selectedProductName && !selectedProductCode) return;
    if (recipeSearchTimer.current) clearTimeout(recipeSearchTimer.current);
    recipeSearchTimer.current = setTimeout(async () => {
      setRecipeSearching(true);
      setPendingRecipe(null);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (selectedProductCode) params.set('productCode', selectedProductCode);
        if (selectedProductName) params.set('productName', selectedProductName);
        const res = await fetch(`/api/coextrusion-orders/recipe-by-product?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.recipe) setPendingRecipe(data.recipe.recipeData || {});
        }
      } catch (_) {}
      finally { setRecipeSearching(false); }
    }, 600);
    return () => { if (recipeSearchTimer.current) clearTimeout(recipeSearchTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductName, selectedProductCode, orderData, hasExisting]);

  const addRow = (which: 'tratada' | 'sello') => {
    if (which === 'tratada') setCaraTratada(prev => [...prev, { ...EMPTY_ROW }]);
    else setCaraSello(prev => [...prev, { ...EMPTY_ROW }]);
  };

  const removeRow = (which: 'tratada' | 'sello', idx: number) => {
    if (which === 'tratada') setCaraTratada(prev => prev.filter((_, i) => i !== idx));
    else setCaraSello(prev => prev.filter((_, i) => i !== idx));
  };

  const totalPctFormulacion = (rows: any[]) =>
    rows.reduce((s, r) => s + (parseFloat(r.pctFormulacion) || 0), 0).toFixed(2);

  const totalPctResina = (rows: any[]) =>
    rows.reduce((s, r) => s + (parseFloat(r.pctResina) || 0), 0).toFixed(4);

  const totalCantidad = (rows: any[]) =>
    rows.reduce((s, r) => s + (parseFloat(r.cantidad) || 0), 0).toFixed(2);

  const buildFormData = () => ({
    productCode: orderData?.order?.productCode,
    productName: selectedProductName || orderData?.order?.productName,
    formulaName,
    ancho,
    calibre,
    color,
    inventario,
    porcentajeCaraTratada: pctCaraTratada,
    porcentajeCaraSello: pctCaraSello,
    factorDesperdicio,
    pesoEstructura,
    rendimiento,
    micraje,
    desperdicio,
    caraTratada,
    caraSello,
    corteLateral,
    refilado,
    refiladoCms,
    tubular,
    pesoBobina,
    diametroBobina,
    metros,
    observaciones,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/coextrusion-orders/${productionOrderId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ formData: buildFormData(), linkedOrderIds, saveAsRecipe }),
      });
      onClose();
    } catch (e) {
      alert('Error al guardar la orden de coextrusión');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    // Save first, then open print view
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/coextrusion-orders/${productionOrderId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ formData: buildFormData(), linkedOrderIds, saveAsRecipe }),
      });
      window.open(`/app/coextrusion-orders/${productionOrderId}/print`, '_blank');
    } catch (e) {
      alert('Error al guardar antes de imprimir');
    } finally {
      setSaving(false);
    }
  };

  // ROT-206: once a coextrusion order has been saved once, the load effect
  // above never re-derives Cara Tratada/Cara Sello or Ancho/Calibre from
  // current materials/Comercia data again (it trusts the saved snapshot
  // verbatim, see the `data.existing` branch above) — even after a later
  // Comercia resync corrects the underlying production_order_materials
  // (e.g. properly splitting/labeling CARA TRATADA vs CARA DE SELLO). A
  // form saved before that correction stays stuck showing the old,
  // materials-blind snapshot forever. This lets a user explicitly discard
  // the saved snapshot and rebuild it from what Comercia/materials show
  // right now, without silently overwriting anything on load.
  const handleRecalculateFromMaterials = () => {
    const mats = orderData?.materials || [];
    if (mats.length === 0) {
      alert('No hay materiales cargados para esta orden.');
      return;
    }
    if (!confirm('Esto reemplazará Cara Tratada, Cara Sello y (si Comercia los tiene) Ancho/Calibre con los valores actuales de materiales/Comercia, descartando lo guardado. ¿Continuar?')) {
      return;
    }
    const { ct, cs, pctTratada, pctSello } = buildPrefillByFace(mats);
    setCaraTratada(ct);
    setCaraSello(cs);
    setPctCaraTratada(pctTratada);
    setPctCaraSello(pctSello);
    if (orderData?.comerciaAncho) setAncho(orderData.comerciaAncho);
    if (orderData?.comerciaCalibre) setCalibre(orderData.comerciaCalibre);
  };

  const toggleLinkedOrder = (id: string) => {
    setLinkedOrderIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
      <div className="bg-white rounded-lg p-8 text-center">
        <div className="animate-spin text-4xl mb-2">⚙️</div>
        <p className="text-gray-600">Cargando orden de coextrusión...</p>
      </div>
    </div>
  );

  const order = orderData?.order;
  const totalKg = parseFloat(order?.quantityPlanned || '0');

  // ── renderFormulaTable: plain function (NOT a component) so React never
  // unmounts/remounts it on parent state changes — keeps input focus while typing.
  const renderFormulaTable = (which: 'tratada' | 'sello') => {
    const rows = which === 'tratada' ? caraTratada : caraSello;
    return (
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-400 px-2 py-1 text-left">MATERIA PRIMA</th>
            <th className="border border-gray-400 px-1 py-1 text-center w-24">% Formulación</th>
            <th className="border border-gray-400 px-1 py-1 text-center w-28">% Resina por capa <span className="font-normal text-gray-400">(auto)</span></th>
            <th className="border border-gray-400 px-1 py-1 text-center w-28">CANTIDAD Kg <span className="font-normal text-gray-400">(auto)</span></th>
            <th className="border border-gray-400 px-1 py-1 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <td className="border border-gray-300 px-1 py-0.5">
                <MatInputWithDropdown
                  value={row.materiaPrima}
                  onChange={val => updateRow(which, i, 'materiaPrima', val)}
                />
              </td>
              <td className="border border-gray-300 px-1 py-0.5">
                <input
                  className="w-full outline-none bg-transparent px-1 text-center text-xs"
                  value={row.pctFormulacion}
                  onChange={e => updateRow(which, i, 'pctFormulacion', e.target.value)}
                  placeholder="0.00"
                  type="number"
                  step="0.01"
                />
              </td>
              <td className="border border-gray-300 px-1 py-0.5 text-center bg-amber-50 font-medium text-amber-800">
                {row.pctResina ? `${parseFloat(row.pctResina).toFixed(2)}%` : '—'}
              </td>
              <td className="border border-gray-300 px-1 py-0.5 text-center bg-blue-50 font-medium text-blue-800">
                {row.cantidad || '—'}
              </td>
              <td className="border border-gray-300 text-center">
                <button onClick={() => removeRow(which, i)} className="text-red-400 hover:text-red-600 px-1 text-xs">✕</button>
              </td>
            </tr>
          ))}
          <tr className="bg-gray-100 font-semibold">
            <td className="border border-gray-400 px-2 py-1 text-right text-xs">TOTAL</td>
            <td className="border border-gray-400 px-1 py-1 text-center text-xs">{totalPctFormulacion(rows)}%</td>
            <td className="border border-gray-400 px-1 py-1 text-center text-xs bg-amber-50 text-amber-800">{parseFloat(totalPctResina(rows)).toFixed(2)}%</td>
            <td className="border border-gray-400 px-1 py-1 text-center text-xs bg-blue-50 text-blue-800">{totalCantidad(rows)} Kg</td>
            <td className="border border-gray-400"></td>
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[80]">
      <div className="bg-white rounded-lg shadow-2xl w-[96vw] max-w-6xl max-h-[94vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-blue-700 text-white rounded-t-lg">
          <div>
            <h2 className="text-lg font-bold">ORDEN DE CO-EXTRUSIÓN</h2>
            <p className="text-xs opacity-80">{order?.orderNumber} · {order?.productName}</p>
          </div>
          <div className="flex items-center gap-2">
            {recipeApplied && (
              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded font-medium">✓ Receta histórica cargada</span>
            )}
            {hasExisting && (
              <span className="bg-amber-400 text-amber-900 text-xs px-2 py-1 rounded font-medium">Editando existente</span>
            )}
            {hasExisting && (
              <button
                type="button"
                onClick={handleRecalculateFromMaterials}
                title="Reemplaza Cara Tratada, Cara Sello y Ancho/Calibre con los valores actuales de materiales/Comercia"
                className="bg-white/10 hover:bg-white/20 text-white text-xs px-2 py-1 rounded font-medium border border-white/30">
                ↻ Recalcular desde Materiales
              </button>
            )}
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-4 text-sm space-y-4">

          {/* ─── Pending recipe banner ─── */}
          {recipeSearching && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded px-3 py-2 text-xs text-blue-700">
              <span className="animate-spin">⚙️</span>
              Buscando receta histórica para este producto...
            </div>
          )}
          {!recipeSearching && pendingRecipe && (
            <div className="flex items-center justify-between bg-green-50 border border-green-300 rounded px-3 py-2 text-xs">
              <div className="flex items-center gap-2 text-green-800">
                <span className="text-base">📋</span>
                <div>
                  <span className="font-bold">Receta histórica encontrada</span>
                  {pendingRecipe.formulaName && <span className="ml-1 text-green-600">— {pendingRecipe.formulaName}</span>}
                  <p className="text-green-600 mt-0.5">Se encontraron materias primas guardadas para este producto. ¿Deseas pre-cargar los datos?</p>
                </div>
              </div>
              <div className="flex gap-2 ml-4 shrink-0">
                <button
                  onClick={() => applyRecipe(pendingRecipe)}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-semibold">
                  Aplicar receta
                </button>
                <button
                  onClick={() => setPendingRecipe(null)}
                  className="bg-white hover:bg-gray-100 text-gray-600 border border-gray-300 px-3 py-1 rounded text-xs">
                  Ignorar
                </button>
              </div>
            </div>
          )}
          {!recipeSearching && recipeApplied && !pendingRecipe && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded px-3 py-2 text-xs text-green-700">
              <span>✓</span>
              <span className="font-semibold">Receta histórica aplicada.</span>
              <span className="text-green-600">Puedes modificar los valores antes de guardar.</span>
            </div>
          )}

          {/* ─── Section 1: Header fields ─── */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left col */}
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-0.5">FÓRMULA / NOMBRE</label>
                <input className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none px-1 py-0.5 font-semibold text-blue-800"
                  value={formulaName} onChange={e => setFormulaName(e.target.value)} placeholder="Ej. RESINA PEMEX SIN ADITIVOS" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-0.5">CLIENTE</label>
                  <input className="w-full border-b border-gray-300 bg-gray-50 outline-none px-1 py-0.5 text-gray-700"
                    value={order?.customerName || ''} readOnly />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-0.5">LOTE</label>
                  <input className="w-full border-b border-gray-300 bg-gray-50 outline-none px-1 py-0.5 text-gray-700"
                    value={order?.lotCode || ''} readOnly />
                </div>
              </div>
              <div className="relative">
                <label className="block text-xs font-bold text-gray-600 mb-0.5">PRODUCTO <span className="font-normal text-blue-600">(editable)</span></label>
                <div className="relative flex items-center">
                  <input
                    ref={productInputRef}
                    className="w-full border-b-2 border-blue-400 focus:border-blue-600 outline-none px-1 py-0.5 text-gray-800 bg-blue-50 pr-6"
                    value={productSearch}
                    onChange={e => { setProductSearch(e.target.value); setSelectedProductName(e.target.value); setSelectedProductCode(''); setProductDropdownOpen(true); }}
                    onFocus={() => setProductDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setProductDropdownOpen(false), 150)}
                    placeholder="Buscar materia prima..."
                  />
                  <Search className="absolute right-1 w-3.5 h-3.5 text-blue-400 pointer-events-none" />
                </div>
                {productDropdownOpen && (
                  <div className="absolute z-50 w-full bg-white border border-gray-300 rounded shadow-lg max-h-44 overflow-y-auto mt-0.5">
                    {productSuggestions
                      .map(m => (
                        <div key={m.id}
                          className="px-2 py-1.5 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0"
                          onMouseDown={() => { setSelectedProductName(m.name); setSelectedProductCode(m.code || ''); setProductSearch(m.name); setProductDropdownOpen(false); }}>
                          <span className="font-medium text-gray-800">{m.name}</span>
                          {m.code && <span className="ml-2 text-gray-400">{m.code}</span>}
                          {m.caliber && <span className="ml-2 text-blue-500">{m.caliber}</span>}
                        </div>
                      ))}
                    {productSuggestions.length === 0 && (
                      <div className="px-2 py-2 text-xs text-gray-400 italic">Sin resultados en catálogo</div>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-0.5">COLOR</label>
                  <input className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none px-1 py-0.5"
                    value={color} onChange={e => setColor(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-0.5">ORDEN INTERNA</label>
                  <input className="w-full border-b border-gray-300 bg-gray-50 outline-none px-1 py-0.5 text-gray-700"
                    value={order?.orderNumber || ''} readOnly />
                </div>
              </div>
            </div>

            {/* Right col */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-0.5">ANCHO (CM)</label>
                  <input className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none px-1 py-0.5"
                    value={ancho} onChange={e => setAncho(e.target.value)} type="number" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-0.5">CALIBRE (GAUGES)</label>
                  <input className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none px-1 py-0.5"
                    value={calibre} onChange={e => setCalibre(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-0.5">O. PRODUCCIÓN (Kg)</label>
                  <input className="w-full border-b border-gray-300 bg-gray-50 outline-none px-1 py-0.5 text-gray-700 font-semibold"
                    value={totalKg.toFixed(2)} readOnly />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-0.5">INVENTARIO (Kg)</label>
                  <input className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none px-1 py-0.5"
                    value={inventario} onChange={e => setInventario(e.target.value)} type="number" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-0.5">FECHA DE ENTREGA</label>
                <input className="w-full border-b border-gray-300 bg-gray-50 outline-none px-1 py-0.5 text-gray-700"
                  value={order?.dueDate || ''} readOnly />
              </div>
              {/* Right-side formula params */}
              <div className="bg-gray-50 rounded border border-gray-200 p-3 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <label className="font-bold text-gray-600">FACTOR DESPERDICIO %</label>
                  <input className="w-full border-b border-gray-300 outline-none focus:border-blue-500 text-center font-semibold"
                    value={factorDesperdicio} onChange={e => setFactorDesperdicio(e.target.value)} type="number" />
                </div>
                <div>
                  <label className="font-bold text-gray-600">DESPERDICIO (Kg)</label>
                  <input className="w-full border-b border-gray-200 bg-blue-50 outline-none text-center font-semibold text-blue-800"
                    value={desperdicio} readOnly />
                </div>
                <div>
                  <label className="font-bold text-gray-600">% CARA TRATADA</label>
                  <input className="w-full border-b border-gray-300 outline-none focus:border-blue-500 text-center"
                    value={pctCaraTratada} onChange={e => setPctCaraTratada(e.target.value)} type="number" />
                </div>
                <div>
                  <label className="font-bold text-gray-600">% CARA DE SELLO</label>
                  <input className="w-full border-b border-gray-300 outline-none focus:border-blue-500 text-center"
                    value={pctCaraSello} onChange={e => setPctCaraSello(e.target.value)} type="number" />
                </div>
                <div>
                  <label className="font-bold text-gray-600">PESO ESTRUCTURA (gr/m²) <span className="font-normal text-gray-400 text-[10px]">= Micraje×0.92</span></label>
                  <input className="w-full border-b border-gray-200 bg-amber-50 outline-none text-center font-semibold text-amber-800"
                    value={pesoEstructura} readOnly />
                </div>
                <div>
                  <label className="font-bold text-gray-600">RENDIMIENTO (m²/kg) <span className="font-normal text-gray-400 text-[10px]">= 1000/Peso</span></label>
                  <input className="w-full border-b border-gray-200 bg-amber-50 outline-none text-center font-semibold text-amber-800"
                    value={rendimiento} readOnly />
                </div>
                <div className="col-span-2">
                  <label className="font-bold text-gray-600">MICRAJE (Micras) <span className="font-normal text-gray-400 text-[10px]">= Calibre÷4</span></label>
                  <input className="w-full border-b border-gray-200 bg-amber-50 outline-none text-center font-semibold text-amber-800"
                    value={micraje} readOnly />
                </div>
              </div>
            </div>
          </div>

          {/* ─── CARA TRATADA ─── */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-gray-700 uppercase tracking-wider text-xs border-b-2 border-gray-400 pb-0.5 flex-1">CARA TRATADA</h3>
              <button onClick={() => addRow('tratada')} className="ml-3 text-xs text-blue-600 hover:underline">+ Fila</button>
            </div>
            {renderFormulaTable('tratada')}
          </div>

          {/* ─── CARA DE SELLO ─── */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-gray-700 uppercase tracking-wider text-xs border-b-2 border-gray-400 pb-0.5 flex-1">CARA DE SELLO</h3>
              <button onClick={() => addRow('sello')} className="ml-3 text-xs text-blue-600 hover:underline">+ Fila</button>
            </div>
            {renderFormulaTable('sello')}
          </div>

          {/* ─── Footer production fields ─── */}
          <div className="grid grid-cols-3 gap-4 border-t pt-3">
            <div className="flex items-center gap-4 col-span-3 flex-wrap">
              <label className="flex items-center gap-1.5 font-semibold text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={corteLateral} onChange={e => setCorteLateral(e.target.checked)} className="w-4 h-4" />
                CORTE LATERAL
              </label>
              <label className="flex items-center gap-1.5 font-semibold text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={refilado} onChange={e => setRefilado(e.target.checked)} className="w-4 h-4" />
                REFILADO
              </label>
              {refilado && (
                <div className="flex items-center gap-1">
                  <input className="w-20 border-b border-gray-400 outline-none text-center text-xs px-1"
                    value={refiladoCms} onChange={e => setRefiladoCms(e.target.value)} placeholder="CMS" />
                  <span className="text-xs text-gray-500">CMS</span>
                </div>
              )}
              <label className="flex items-center gap-1.5 font-semibold text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={tubular} onChange={e => setTubular(e.target.checked)} className="w-4 h-4" />
                TUBULAR
              </label>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-0.5">PESO DE BOBINA (Kg)</label>
              <input className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none px-1 py-0.5"
                value={pesoBobina} onChange={e => setPesoBobina(e.target.value)} type="number" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-0.5">DIÁMETRO BOBINA</label>
              <input className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none px-1 py-0.5"
                value={diametroBobina} onChange={e => setDiametroBobina(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-0.5">METROS</label>
              <input className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none px-1 py-0.5"
                value={metros} onChange={e => setMetros(e.target.value)} type="number" />
            </div>
            <div className="col-span-3">
              <label className="block text-xs font-bold text-gray-600 mb-0.5">OBSERVACIONES</label>
              <textarea className="w-full border border-gray-300 rounded outline-none focus:border-blue-500 px-2 py-1 text-xs resize-none"
                rows={2} value={observaciones} onChange={e => setObservaciones(e.target.value)} />
            </div>
          </div>

          {/* ─── Link to production orders ─── */}
          <div className="border-t pt-3">
            <button onClick={() => setShowLinkPanel(p => !p)}
              className="flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900">
              <Link2 className="w-4 h-4" />
              Vincular a Órdenes de Producción ({linkedOrderIds.length} seleccionadas)
              {showLinkPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showLinkPanel && (
              <div className="mt-2 border rounded bg-gray-50">
                <div className="px-2 pt-2 pb-1 border-b border-gray-200">
                  <div className="relative flex items-center">
                    <Search className="absolute left-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    <input
                      className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded outline-none focus:border-blue-500 bg-white"
                      placeholder="Buscar por número de orden o producto..."
                      value={linkSearch}
                      onChange={e => setLinkSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto p-2 grid grid-cols-2 gap-1">
                  {linkableOrders.length === 0 && (
                    <p className="text-xs text-gray-500 col-span-2">No hay órdenes disponibles para vincular</p>
                  )}
                  {linkableOrders
                    .filter(o => !linkSearch ||
                      o.orderNumber?.toLowerCase().includes(linkSearch.toLowerCase()) ||
                      o.productName?.toLowerCase().includes(linkSearch.toLowerCase()))
                    .map(o => (
                      <label key={o.id} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-blue-50 p-1 rounded">
                        <input type="checkbox" checked={linkedOrderIds.includes(o.id)} onChange={() => toggleLinkedOrder(o.id)} />
                        <span className="font-medium">{o.orderNumber}</span>
                        <span className="text-gray-500 truncate">{o.productName}</span>
                      </label>
                    ))}
                  {linkableOrders.length > 0 && linkableOrders.filter(o => !linkSearch ||
                    o.orderNumber?.toLowerCase().includes(linkSearch.toLowerCase()) ||
                    o.productName?.toLowerCase().includes(linkSearch.toLowerCase())).length === 0 && (
                    <p className="text-xs text-gray-400 col-span-2 italic">Sin resultados para "{linkSearch}"</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ─── Save as recipe ─── */}
          <div className="flex items-center gap-2">
            <input type="checkbox" id="saveRecipe" checked={saveAsRecipe} onChange={e => setSaveAsRecipe(e.target.checked)} className="w-4 h-4" />
            <label htmlFor="saveRecipe" className="text-xs text-gray-700 cursor-pointer">
              Guardar fórmulas como <strong>receta histórica</strong> para este producto (se auto-llenará en futuras importaciones)
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-5 py-3 flex justify-between items-center rounded-b-lg">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint} disabled={saving}
              className="border-gray-400 text-gray-700 hover:bg-gray-100">
              <Printer className="w-4 h-4 mr-1" />
              Guardar e Imprimir
            </Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-blue-700 text-white hover:bg-blue-800">
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
